import {
    SearchQuery,
    setSearchQuery,
    getSearchQuery,
    findNext,
    findPrevious,
    replaceNext,
    replaceAll,
    selectMatches,
    closeSearchPanel,
    openSearchPanel,
    searchPanelOpen
} from '@codemirror/search';
import { runScopeHandlers } from '@codemirror/view';

export const searchPhrases = {
    en: {
        Find: 'Find',
        Replace: 'Replace',
        next: 'Next',
        previous: 'Prev',
        all: 'All',
        'match case': 'Case',
        regexp: 'Regex',
        'by word': 'Whole word',
        replace: 'Replace',
        'replace all': 'Replace all',
        close: 'Close',
        'current match': 'Current match',
        'on line': 'on line',
        'replaced match on line $': 'Replaced match on line $',
        'replaced $ matches': 'Replaced $ matches'
    },
    zh: {
        Find: '查找',
        Replace: '替换',
        next: '下一个',
        previous: '上一个',
        all: '全部',
        'match case': '区分大小写',
        regexp: '正则',
        'by word': '全词匹配',
        replace: '替换',
        'replace all': '全部替换',
        close: '关闭',
        'current match': '当前匹配',
        'on line': '第',
        'replaced match on line $': '已替换第 $ 行的匹配',
        'replaced $ matches': '已替换 $ 处匹配'
    }
};

export const toggleSearchPanel = (view) => {
    if (searchPanelOpen(view.state)) {
        return closeSearchPanel(view);
    }
    return openSearchPanel(view);
};

export class FastSearchPanel {
    constructor(view, labels) {
        this.view = view;
        this.labels = labels;
        this.query = getSearchQuery(view.state);
        this.commit = this.commit.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);

        this.searchField = this.createTextInput('search', labels.Find, true);
        this.replaceField = this.createTextInput('replace', labels.Replace);
        this.caseField = this.createCheckbox('case', labels['match case']);
        this.reField = this.createCheckbox('re', labels.regexp);
        this.wordField = this.createCheckbox('word', labels['by word']);

        this.dom = document.createElement('div');
        this.dom.className = 'cm-search fd-search-panel';
        this.dom.addEventListener('keydown', this.handleKeydown);

        const rowPrimary = document.createElement('div');
        rowPrimary.className = 'fd-search-row fd-search-row-primary';
        rowPrimary.append(
            this.searchField,
            this.createButton('next', labels.next, () => findNext(view)),
            this.createButton('prev', labels.previous, () => findPrevious(view)),
            this.createButton('select', labels.all, () => selectMatches(view)),
            this.caseField.wrapper,
            this.reField.wrapper,
            this.wordField.wrapper,
            this.createIconButton(labels.close, () => closeSearchPanel(view))
        );

        this.dom.append(rowPrimary);

        if (!view.state.readOnly) {
            const rowReplace = document.createElement('div');
            rowReplace.className = 'fd-search-row fd-search-row-replace';
            rowReplace.append(
                this.replaceField,
                this.createButton('replace', labels.replace, () => replaceNext(view)),
                this.createButton('replaceAll', labels['replace all'], () => replaceAll(view))
            );
            this.dom.append(rowReplace);
        }

        this.setQuery(this.query);
    }

    createTextInput(name, placeholder, isMainField = false) {
        const input = document.createElement('input');
        input.type = 'text';
        input.name = name;
        input.placeholder = placeholder;
        input.setAttribute('aria-label', placeholder);
        if (isMainField) {
            input.setAttribute('main-field', 'true');
        }
        input.className = `fd-search-input fd-search-input-${name}`;
        input.addEventListener('input', this.commit);
        return input;
    }

    createCheckbox(name, labelText) {
        const wrapper = document.createElement('label');
        wrapper.className = 'fd-search-toggle';

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.name = name;
        input.addEventListener('change', this.commit);

        const text = document.createElement('span');
        text.textContent = labelText;

        wrapper.append(input, text);
        return { wrapper, input };
    }

    createButton(name, text, onClick) {
        const button = document.createElement('button');
        button.type = 'button';
        button.name = name;
        button.className = 'fd-search-btn';
        button.textContent = text;
        button.addEventListener('click', onClick);
        return button;
    }

    createIconButton(label, onClick) {
        const button = document.createElement('button');
        button.type = 'button';
        button.name = 'close';
        button.className = 'fd-search-icon-btn';
        button.setAttribute('aria-label', label);
        button.textContent = '×';
        button.addEventListener('click', onClick);
        return button;
    }

    commit() {
        const query = new SearchQuery({
            search: this.searchField.value,
            caseSensitive: this.caseField.input.checked,
            regexp: this.reField.input.checked,
            wholeWord: this.wordField.input.checked,
            replace: this.replaceField?.value || ''
        });

        if (!query.eq(this.query)) {
            this.query = query;
            this.view.dispatch({ effects: setSearchQuery.of(query) });
        }
    }

    handleKeydown(event) {
        if (runScopeHandlers(this.view, event, 'search-panel')) {
            event.preventDefault();
            return;
        }

        if (event.key === 'Enter' && event.target === this.searchField) {
            event.preventDefault();
            (event.shiftKey ? findPrevious : findNext)(this.view);
        } else if (event.key === 'Enter' && event.target === this.replaceField) {
            event.preventDefault();
            replaceNext(this.view);
        }
    }

    update(update) {
        for (const tr of update.transactions) {
            for (const effect of tr.effects) {
                if (effect.is(setSearchQuery) && !effect.value.eq(this.query)) {
                    this.setQuery(effect.value);
                }
            }
        }
    }

    setQuery(query) {
        this.query = query;
        this.searchField.value = query.search;
        this.replaceField.value = query.replace;
        this.caseField.input.checked = query.caseSensitive;
        this.reField.input.checked = query.regexp;
        this.wordField.input.checked = query.wholeWord;
    }

    mount() {
        this.searchField.focus();
        this.searchField.select();
    }

    destroy() {
        this.dom.removeEventListener('keydown', this.handleKeydown);
    }

    get pos() { return 80; }
    get top() { return true; }
}
